import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';

import type { ModelName } from '../data/models';
import type { ThinkingChoice } from '../data/thinking-levels';
import type { PickerPolicy } from '../data-policy/picker-policy';
import type { AttachedFile } from '../types';

import { Composer } from './Composer';

interface EmptyStateProps {
  name: string;
  /** Time-of-day greeting prefix, e.g. "Good afternoon". */
  greeting: string;
  modelId: string;
  onModelChange: (modelId: string, name?: ModelName) => void;
  onSend: (text: string, files?: AttachedFile[]) => void;
  /** The chat's thinking choice and Agentic switch, and their setters (see Composer). */
  thinking?: ThinkingChoice | null;
  onThinkingChange?: (choice: ThinkingChoice) => void;
  agentic?: boolean;
  onAgenticChange?: (on: boolean) => void;
  /** The chat's model as picked (see Composer). */
  modelName?: ModelName;
  policy?: PickerPolicy;
  /**
   * 'page' (default) = full-screen Main: big time-based greeting + subtitle.
   * 'panel' = compact side panel: a short single-line greeting (per Figma),
   * sized so it doesn't overwhelm the ~360px panel.
   */
  variant?: 'page' | 'panel';
}

/**
 * First-run state for a new chat: a greeting with the composer directly beneath
 * (per the AI CIO spec). The full page shows a time-based greeting + subtitle;
 * the compact panel shows the short Figma greeting only.
 */
export function EmptyState({
  name,
  greeting,
  modelId,
  onModelChange,
  onSend,
  thinking,
  onThinkingChange,
  agentic,
  onAgenticChange,
  modelName,
  policy,
  variant = 'page',
}: EmptyStateProps) {
  const { t } = useTranslation('ai-cio');
  const isPanel = variant === 'panel';

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center',
        isPanel ? 'max-w-full gap-4' : 'max-w-[620px] gap-6',
      )}
    >
      <div className="text-center">
        <h1 className={cn('font-bold text-foreground', isPanel ? 'text-lg' : 'text-2xl')}>
          {isPanel
            ? t('greeting.panelTitle', 'Hi, how can I help you today?')
            : t('greeting.title', '{{greeting}}, {{name}}!', { greeting, name })}
        </h1>
        {!isPanel && (
          <p className="mt-2 text-sm text-muted-foreground">
            {t(
              'greeting.subtitle',
              'Your AI Chief Investment Officer, ask about portfolio performance, market analysis, or upload documents.',
            )}
          </p>
        )}
      </div>

      <Composer
        className="w-full"
        autoFocusInput
        modelId={modelId}
        onModelChange={onModelChange}
        onSend={onSend}
        thinking={thinking}
        onThinkingChange={onThinkingChange}
        agentic={agentic}
        onAgenticChange={onAgenticChange}
        modelName={modelName}
        policy={policy}
      />
    </div>
  );
}
